import { useCallback, useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem,
  Textarea,

} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { supabase } from 'common/src/supabase';
import { toast } from 'react-toastify';
import { PageHeader, PageLayout, useConfirm } from '../../../components/ui';

interface ExcelRule {
  id: number;
  created_at: string;
  receiver_id: number;
  exporter_id: number;
  variable: string;
  expression: string;
  function: string;
  receiver?: { name: string };
  exporter?: { name: string };
}

const SuperadminExcelRules = () => {
  const [rules, setRules] = useState<ExcelRule[]>([]);
  const confirm = useConfirm();
  const [exporters, setExporters] = useState<any[]>([]);
  const [receivers, setReceivers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<ExcelRule | null>(null);
  const [newRule, setNewRule] = useState<Partial<ExcelRule>>({});
  const [filterReceiver, setFilterReceiver] = useState<string>('');
  const [filterExporter, setFilterExporter] = useState<string>('');
  const [filterVariable, setFilterVariable] = useState<string>('');
  const [isTestOpen, setIsTestOpen] = useState(false);
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<{ matches: boolean; output: string | null }>({ matches: false, output: null });
  const [isGeneralTestOpen, setIsGeneralTestOpen] = useState(false);
  const [generalTestInput, setGeneralTestInput] = useState('');
  const [generalTestReceiver, setGeneralTestReceiver] = useState<string>('');
  const [generalTestExporter, setGeneralTestExporter] = useState<string>('');
  const [generalTestResults, setGeneralTestResults] = useState<Array<{
    rule: ExcelRule;
    matches: boolean;
    output: string | null;
  }>>([]);

  const filteredRules = rules.filter((rule) => {
    const matchReceiver = !filterReceiver || rule.receiver_id.toString() === filterReceiver;
    const matchExporter = !filterExporter || rule.exporter_id.toString() === filterExporter;
    const matchVariable = !filterVariable || rule.variable === filterVariable;
    return matchReceiver && matchExporter && matchVariable;
  });

  const fetchData = useCallback(async () => {
    try {
      const [rulesResponse, exportersResponse, receiversResponse] = await Promise.all([
        supabase
          .from('excel_rules')
          .select('*, receiver:receivers(name), exporter:exporters(name)'),
        supabase.from('exporters').select('*'),
        supabase.from('receivers').select('*'),
      ]);

      if (rulesResponse.error) throw rulesResponse.error;
      if (exportersResponse.error) throw exportersResponse.error;
      if (receiversResponse.error) throw receiversResponse.error;

      setRules(rulesResponse.data);
      setExporters(exportersResponse.data);
      setReceivers(receiversResponse.data);
    } catch (error: any) {
      toast.error('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (rule: ExcelRule) => {
    console.log(rule);
    
    setSelectedRule(rule);
    setIsEditOpen(true);
  };

  const handleDelete = async (rule: ExcelRule) => {
    if (
      await confirm({
        title: 'Delete this rule?',
        confirmLabel: 'Delete',
        isDestructive: true,
      })
    ) {
      try {
        const { error } = await supabase
          .from('excel_rules')
          .delete()
          .eq('id', rule.id);
        if (error) throw error;
        toast.success('Rule deleted successfully');
        fetchData();
      } catch (error: any) {
        toast.error('Error deleting rule: ' + error.message);
      }
    }
  };

  const handleSave = async () => {
    if (!selectedRule) return;
    try {
      const { error } = await supabase
        .from('excel_rules')
        .update({
          receiver_id: selectedRule.receiver_id,
          exporter_id: selectedRule.exporter_id,
          variable: selectedRule.variable,
          expression: selectedRule.expression,
          function: selectedRule.function,
        })
        .eq('id', selectedRule.id);

      if (error) throw error;
      toast.success('Rule updated successfully');
      setIsEditOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error('Error updating rule: ' + error.message);
    }
  };

  const handleCreate = async () => {
    try {
      console.log(newRule);
      const { error } = await supabase.from('excel_rules').insert([newRule]);
      if (error) throw error;
      toast.success('Rule created successfully');
      setIsNewOpen(false);
      setNewRule({});
      fetchData();
    } catch (error: any) {
      toast.error('Error creating rule: ' + error.message);
    }
  };

  const handleDuplicate = (rule: ExcelRule) => {
    setNewRule({
      receiver_id: rule.receiver_id,
      exporter_id: rule.exporter_id,
      variable: rule.variable,
      expression: rule.expression,
      function: rule.function,
    });
    setIsNewOpen(true);
  };

  const testRule = (rule: ExcelRule, input: string) => {
    try {
      const regex = new RegExp(rule.expression);
      const matches = regex.test(input);
      let output = null;

      if (matches && rule.function) {
        if (!rule.function.includes('result =')) {
          output = rule.function;
        } else {
          const sandbox = {
            input,
            matches: input.match(regex),
            result: null
          };
          
          const functionBody = `
            const match = matches;
            ${rule.function}
          `;
          new Function('input', 'matches', functionBody).call(sandbox, input, sandbox.matches);
          output = sandbox.result;
        }
      }

      setTestResult({ matches, output });
    } catch (error) {
      toast.error('Error testing rule: Invalid regex or function');
      setTestResult({ matches: false, output: null });
    }
  };

  const testAllRules = (input: string, receiverId: string, exporterId: string) => {
    const applicableRules = rules.filter(rule => 
      (!receiverId || rule.receiver_id.toString() === receiverId) &&
      (!exporterId || rule.exporter_id.toString() === exporterId)
    );

    const results = applicableRules.map(rule => {
      try {
        const regex = new RegExp(rule.expression);
        const matches = regex.test(input);
        let output = null;

        if (matches && rule.function) {
          if (!rule.function.includes('result =')) {
            output = rule.function;
          } else {
            const sandbox = {
              input,
              matches: input.match(regex),
              result: null
            };
            
            const functionBody = `
              const match = matches;
              ${rule.function}
            `;
            new Function('input', 'matches', functionBody).call(sandbox, input, sandbox.matches);
            output = sandbox.result;
          }
        }

        return { rule, matches, output };
      } catch (error) {
        return { rule, matches: false, output: null };
      }
    });

    setGeneralTestResults(results);
  };

  const columns = [
    { name: 'ID', uid: 'id' },
    { name: 'Recibidor', uid: 'receiver' },
    { name: 'Exportadora', uid: 'exporter' },
    { name: 'Dato a extraer', uid: 'variable' },
    { name: 'Igual a', uid: 'expression' },
    { name: 'Transformar', uid: 'function' },
    { name: 'Acciones', uid: 'actions' },
  ];

  const renderCell = (rule: ExcelRule, columnKey: React.Key) => {
    switch (columnKey) {
      case 'receiver':
        return rule.receiver?.name;
      case 'exporter':
        return rule.exporter?.name;
      case 'actions':
        return (
          <div className="flex items-center gap-2">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onClick={() => handleEdit(rule)}
            >
              <Icon icon='tabler:edit' className="text-default-400" />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onClick={() => handleDuplicate(rule)}
            >
              <Icon icon='tabler:copy' className="text-default-400" />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onClick={() => handleDelete(rule)}
            >
              <Icon icon='tabler:trash' className="text-danger-400" />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onClick={() => {
                setSelectedRule(rule);
                setIsTestOpen(true);
                setTestInput('');
                setTestResult({ matches: false, output: null });
              }}
            >
              🧪
            </Button>
          </div>
        );
      default:
        return rule[columnKey as keyof ExcelRule];
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Reglas de Excel"
        actions={
          <>
            <Button
              variant="flat"
              onClick={() => setIsGeneralTestOpen(true)}
            >
              Probar Reglas
            </Button>
            <Button
              color="primary"
              onClick={() => setIsNewOpen(true)}
            >
              Nueva Regla
            </Button>
          </>
        }
      />

      <div className="flex gap-4 mb-4">
        <Select
          label="Filtrar por Recibidor"
          selectedKeys={filterReceiver ? [filterReceiver] : []}
          onChange={(e) => setFilterReceiver(e.target.value)}
          className="w-64"
        >
          <SelectItem key="" value="">Todos</SelectItem>
          {receivers.map((receiver) => (
            <SelectItem key={receiver.id} value={receiver.id.toString()}>
              {receiver.name}
            </SelectItem>
          ))}
        </Select>

        <Select
          label="Filtrar por Exportadora"
          selectedKeys={filterExporter ? [filterExporter] : []}
          onChange={(e) => setFilterExporter(e.target.value)}
          className="w-64"
        >
          <SelectItem key="" value="">Todas</SelectItem>
          {exporters.map((exporter) => (
            <SelectItem key={exporter.id} value={exporter.id.toString()}>
              {exporter.name}
            </SelectItem>
          ))}
        </Select>

        <Select
          label="Filtrar por Dato a extraer"
          selectedKeys={filterVariable ? [filterVariable] : []}
          onChange={(e) => setFilterVariable(e.target.value)}
          className="w-64"
        >
          <SelectItem key="" value="">Todos</SelectItem>
          <SelectItem key="caliber" value="caliber">Calibre</SelectItem>
          <SelectItem key="color" value="color">Color</SelectItem>
          <SelectItem key="label" value="label">Etiqueta</SelectItem>
          <SelectItem key="format" value="format">Formato</SelectItem>
          <SelectItem key="variety" value="variety">Variedad</SelectItem>
          <SelectItem key="unit_weight" value="unit_weight">Peso unitario</SelectItem>
        </Select>
      </div>

      <Table aria-label="Excel rules table">
        <TableHeader columns={columns}>
          {(column) => <TableColumn key={column.uid}>{column.name}</TableColumn>}
        </TableHeader>
        <TableBody items={filteredRules}>
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)}>
        <ModalContent>
          <ModalHeader>Edit Rule</ModalHeader>
          <ModalBody>
            <Select
              label="Recibidor"
              selectedKeys={selectedRule?.receiver_id ? [selectedRule.receiver_id.toString()] : []}
              onChange={(e) =>
                setSelectedRule({
                  ...selectedRule!,
                  receiver_id: parseInt(e.target.value),
                })
              }
            >
              {receivers.map((receiver) => (
                <SelectItem key={receiver.id} value={receiver.id}>
                  {receiver.name}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Exportadora"
              selectedKeys={selectedRule?.exporter_id ? [selectedRule.exporter_id.toString()] : []}
              onChange={(e) =>
                setSelectedRule({
                  ...selectedRule!,
                  exporter_id: parseInt(e.target.value),
                })
              }
            >
              {exporters.map((exporter) => (
                <SelectItem key={exporter.id} value={exporter.id}>
                  {exporter.name}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Variable a extraer"
              selectedKeys={selectedRule?.variable ? [selectedRule.variable] : []}
              onChange={(e) =>
                setSelectedRule({
                  ...selectedRule!,
                  variable: e.target.value,
                })
              }
            >
              <SelectItem key="caliber" value="caliber">Calibre</SelectItem>
              <SelectItem key="color" value="color">Color</SelectItem>
              <SelectItem key="label" value="label">Etiqueta</SelectItem>
              <SelectItem key="format" value="format">Formato</SelectItem>
              <SelectItem key="variety" value="variety">Variedad</SelectItem>
              <SelectItem key="unit_weight" value="unit_weight">Peso unitario</SelectItem>
            </Select>

            <Input
              label="Igual A"
              value={selectedRule?.expression || ''}
              onChange={(e) =>
                setSelectedRule({
                  ...selectedRule!,
                  expression: e.target.value,
                })
              }
            />

            <Textarea
              label="Transformar"
              value={selectedRule?.function || ''}
              onChange={(e) =>
                setSelectedRule({
                  ...selectedRule!,
                  function: e.target.value,
                })
              }
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isNewOpen} onClose={() => setIsNewOpen(false)}>
        <ModalContent>
          <ModalHeader>New Rule</ModalHeader>
          <ModalBody>
            <Select
              label="Receiver"
              selectedKeys={newRule?.receiver_id ? [newRule.receiver_id.toString()] : []}
              onChange={(e) =>
                setNewRule({
                  ...newRule,
                  receiver_id: parseInt(e.target.value),
                })
              }
            >
              {receivers.map((receiver) => (
                <SelectItem key={receiver.id} value={receiver.id.toString()}>
                  {receiver.name}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Exporter"
              selectedKeys={newRule?.exporter_id ? [newRule.exporter_id.toString()] : []}
              onChange={(e) =>
                setNewRule({
                  ...newRule,
                  exporter_id: parseInt(e.target.value),
                })
              }
            >
              {exporters.map((exporter) => (
                <SelectItem key={exporter.id} value={exporter.id.toString()}>
                  {exporter.name}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Variable a extraer"
              selectedKeys={newRule?.variable ? [newRule.variable] : []}
              onChange={(e) =>
                setNewRule({
                  ...newRule,
                  variable: e.target.value,
                })
              }
            >
              <SelectItem key="calibre" value="caliber">Calibre</SelectItem>
              <SelectItem key="color" value="color">Color</SelectItem>
              <SelectItem key="label" value="label">Etiqueta</SelectItem>
              <SelectItem key="format" value="format">Formato</SelectItem>
              <SelectItem key="variety" value="variety">Variedad</SelectItem>
              <SelectItem key="unit_weight" value="unit_weight">Peso unitario</SelectItem>
            </Select>

            <Input
              label="Expression"
              value={newRule?.expression || ''}
              onChange={(e) =>
                setNewRule({
                  ...newRule,
                  expression: e.target.value,
                })
              }
            />

            <Textarea
              label="Function"
              value={newRule?.function || ''}
              onChange={(e) =>
                setNewRule({
                  ...newRule,
                  function: e.target.value,
                })
              }
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsNewOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleCreate}>
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isTestOpen} onClose={() => setIsTestOpen(false)}>
        <ModalContent>
          <ModalHeader>Test Rule</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div className="bg-default-100 p-3 rounded">
                <p><strong>Variable:</strong> {selectedRule?.variable}</p>
                <p><strong>Expression:</strong> {selectedRule?.expression}</p>
                <p><strong>Function:</strong> {selectedRule?.function}</p>
              </div>
              
              <Input
                label="Test Input"
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
              />

              <Button 
                color="primary" 
                onClick={() => selectedRule && testRule(selectedRule, testInput)}
              >
                Test
              </Button>

              {testResult.matches !== null && (
                <div className={`p-3 rounded ${testResult.matches ? 'bg-green-100' : 'bg-red-100'}`}>
                  <p><strong>Matches:</strong> {testResult.matches ? 'Yes' : 'No'}</p>
                  {testResult.matches && testResult.output !== null && (
                    <p><strong>Output:</strong> {testResult.output}</p>
                  )}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsTestOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isGeneralTestOpen} onClose={() => setIsGeneralTestOpen(false)} size="2xl">
        <ModalContent>
          <ModalHeader>Probar Todas las Reglas</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Select
                label="Recibidor (opcional)"
                selectedKeys={generalTestReceiver ? [generalTestReceiver] : []}
                onChange={(e) => setGeneralTestReceiver(e.target.value)}
              >
                <SelectItem key="" value="">Todos</SelectItem>
                {receivers.map((receiver) => (
                  <SelectItem key={receiver.id} value={receiver.id.toString()}>
                    {receiver.name}
                  </SelectItem>
                ))}
              </Select>

              <Select
                label="Exportadora (opcional)"
                selectedKeys={generalTestExporter ? [generalTestExporter] : []}
                onChange={(e) => setGeneralTestExporter(e.target.value)}
              >
                <SelectItem key="" value="">Todas</SelectItem>
                {exporters.map((exporter) => (
                  <SelectItem key={exporter.id} value={exporter.id.toString()}>
                    {exporter.name}
                  </SelectItem>
                ))}
              </Select>

              <Input
                label="Texto a probar"
                value={generalTestInput}
                onChange={(e) => setGeneralTestInput(e.target.value)}
              />

              <Button 
                color="primary" 
                onClick={() => testAllRules(generalTestInput, generalTestReceiver, generalTestExporter)}
              >
                Probar
              </Button>

              {generalTestResults.length > 0 && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {generalTestResults.map((result, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded border ${
                        result.matches ? 'border-green-500 bg-green-50' : 'border-default-300 bg-default-50'
                      }`}
                    >
                      <p><strong>Recibidor:</strong> {result.rule.receiver?.name}</p>
                      <p><strong>Exportadora:</strong> {result.rule.exporter?.name}</p>
                      <p><strong>Variable:</strong> {result.rule.variable}</p>
                      <p><strong>Expresión:</strong> {result.rule.expression}</p>
                      <p><strong>Coincide:</strong> {result.matches ? 'Sí' : 'No'}</p>
                      {result.matches && (
                        <p><strong>Resultado:</strong> {result.output}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsGeneralTestOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminExcelRules; 